<script lang="ts">
  import { page } from "$app/state";
  import { goto } from "$app/navigation";
  import {
    ScrollText,
    UserRoundSearch,
    Microscope,
    SearchCode,
    SendHorizontal,
    List,
    Notebook,
    FileSpreadsheet,
    Library,
    FileText,
    Globe,
    ShoppingCart,
    Users,
    ChevronDown,
    ChevronRight,
    Feather,
    BookOpen,
    Building,
    FileUser,
    Bell,
    Radar,
  } from "lucide-svelte";
  import { SVG } from "$lib/components";
  import { routes } from "$lib/routes";
  import { cn } from "$lib/utility";
  import Tooltip from "$lib/components/basic/tooltip/tooltip.svelte";
  import { authStore as Current } from "$lib/stores";
  import { SUBSCRIPTION_TIER } from "$lib/types";
  import { pricingIcon2, upgradeIcon, companyIcon, sheetIcon } from "$lib/images/icons";
  import { isDevelopment } from "$lib/utility/environment";
  import { Sidebar } from "$lib/components/ui";
  import CreditsPreviewDialog from "$lib/components/dialogs/creditsPreviewDialog.svelte";
  import CreditsMenuItem from "./creditsNavItem.svelte";
  import * as Popover from "$lib/components/ui/popover";
  import { sidePanelStore } from "$lib/stores/sidePanelStore";
  import { onMount } from "svelte";
  import { slide } from "svelte/transition";
  import ShortcutKey from "$lib/table/components/ShortcutKey.svelte";
  import { tableStore } from "$lib/stores/table-store";
  import NotificationsDialog from "$lib/components/dialogs/notificationsDialog.svelte";

  let sidebar = Sidebar.useSidebar();

  let { isCollapsed, borderColor, handleSidebarNavigation, handleUpgradeClick, onlyItems, showAdminContent } = $props<{
    isCollapsed: boolean;
    borderColor: string;
    handleSidebarNavigation: () => void;
    handleUpgradeClick: () => void;
    showAdminContent?: boolean;
    onlyItems?: Array<string | NavigationItemConfig>;
  }>();

  let isCreditsOpen = $state(false);
  let isNotificationsOpen = $state(false);
  let openSubmenuId = $state<string | null>(null);
  let currentPath = $state(page.url.pathname);

  interface NavigationIcon {
    component: any;
    strokeWidth?: number;
    size?: number;
    isCustom?: boolean;
  }

  interface SubmenuItem {
    href: string;
    label: string;
    icon?: NavigationIcon;
    description?: string;
    onClick?: (e: MouseEvent) => void;
  }

  interface NavigationItemConfig {
    id: string;
    href: string;
    label: string;
    icon?: NavigationIcon;
    view?: any;
    onClick?: (e: MouseEvent) => void;
    isDisplayed?: boolean;
    tooltipContent?: string;
    inDevelopment?: boolean;
    isActive?: boolean;
    extraProps?: Record<string, any>;
    tooltipComponent?: any;
    tooltipProps?: Record<string, any>;
    isCollapsible?: boolean;
    submenu?: SubmenuItem[];
    altShortcutHotKey?: string;
    showLabel?: boolean;
    showTooltip?: boolean;
  }

  const isSearchPage = $derived(page.url.pathname === "/search" || page.url.pathname.startsWith("/search/"));
  const isSearchV2Page = $derived(page.url.pathname === "/search-dev" || page.url.pathname.startsWith("/search-dev/"));
  const isTemplatesPage = $derived(page.url.pathname === "/templates" || page.url.pathname.startsWith("/templates/"));
  const isCampaignPage = $derived(page.url.pathname === "/campaigns" || page.url.pathname.startsWith("/campaigns/"));
  const isListsPage = $derived(page.url.pathname === "/lists" || page.url.pathname.startsWith("/lists/"));
  const isTablesPage = $derived(page.url.pathname === "/table" || page.url.pathname.startsWith("/table/"));
  const isApprovalsPage = $derived(page.url.pathname.startsWith("/outbox"));
  const isRootPage = $derived(page.url.pathname === "/");
  const isNotebookPage = $derived(page.url.pathname === "/notebook" || page.url.pathname.startsWith("/notebook/"));
  const isFeaturesPage = $derived(page.url.pathname.startsWith("/features"));
  const isFeaturesSubpage = $derived(page.url.pathname.startsWith("/features/"));
  const isUseCasesPage = $derived(page.url.pathname.startsWith("/use-cases"));
  const isUseCasesSubpage = $derived(page.url.pathname.startsWith("/use-cases/"));
  const isAboutPage = $derived(page.url.pathname === "/about");
  const isBlogPage = $derived(page.url.pathname === "/blog" || page.url.pathname.startsWith("/blog/"));
  const isAdminPage = $derived(page.url.pathname === "/admin" || page.url.pathname.startsWith("/admin/"));

  const isOnPdfProcessingPage = $derived(page.url.pathname.startsWith("/features/ai-pdf-processing"));
  const isOnWebScrapingPage = $derived(page.url.pathname.startsWith("/features/ai-web-scraping"));
  const isOnEcommercePage = $derived(page.url.pathname.startsWith("/use-cases/ai-ecommerce-automation"));

  function isSubmenuItemActive(href: string): boolean {
    return currentPath.startsWith(href);
  }

  function getNavItemClasses(inDevelopment?: boolean, isActive?: boolean, isSubmenuItem?: boolean) {
    return cn(
      "sidebar-menuitem font-light hover:font-semibold transition-default border-t h-12",
      borderColor,
      isCollapsed ? "flex items-center justify-center" : "px-6",
      inDevelopment ? "" : "",
      isActive ? "bg-black dark:bg-theme-green text-white dark:text-black" : "",
      isSubmenuItem ? "pl-10 border-t-0" : "",
    );
  }

  function getIconClasses() {
    return cn(
      "sidebar-icon regular transition-default flex items-center justify-center",
      isCollapsed ? "h-5 w-5" : "h-6 w-6",
    );
  }

  function renderIcon(icon: NavigationIcon) {
    if (icon.isCustom) {
      return {
        Component: SVG,
        props: {
          className: getIconClasses(),
          file: icon.component,
        },
      };
    }

    const iconSize = isCollapsed ? 20 : 24;
    return {
      Component: icon.component,
      props: {
        size: icon.size || iconSize,
        strokeWidth: icon.strokeWidth || 1,
        class: getIconClasses(),
      },
    };
  }

  function handleNavigation(e: MouseEvent, route: string) {
    e.preventDefault();
    handleSidebarNavigation();
    goto(route);
  }

  const handleSearchNavigation = (e: MouseEvent) =>
    handleNavigation(e, $Current.isLoggedIn ? routes.searches : routes.root);
  const handleSearchDevNavigation = (e: MouseEvent) => handleNavigation(e, routes.searchesDev);
  const handleTemplatesNavigation = (e: MouseEvent) => handleNavigation(e, routes.templates);
  const handleTablesNavigation = (e: MouseEvent) =>
    handleNavigation(e, $Current.isLoggedIn ? routes.tables : routes.root);
  const handleCampaignNavigation = (e: MouseEvent) => handleNavigation(e, routes.campaigns);
  const handleSandboxNavigation = (e: MouseEvent) => handleNavigation(e, routes.sandbox);
  const handleListNavigation = (e: MouseEvent) => handleNavigation(e, routes.lists);
  const handleNotebookNavigation = (e: MouseEvent) => handleNavigation(e, routes.notebook);
  const handleAboutNavigation = (e: MouseEvent) => handleNavigation(e, "/about");
  const handleBlogNavigation = (e: MouseEvent) => handleNavigation(e, routes.blog);
  const handlePricingNavigation = (e: MouseEvent) => handleNavigation(e, routes.pricing);
  const handleAdminNavigation = (e: MouseEvent) => handleNavigation(e, routes.admin);

  function handleUpgradeNavigation(e: MouseEvent) {
    e.preventDefault();
    handleUpgradeClick();
  }

  function toggleSubmenu(e: MouseEvent, id: string) {
    e.preventDefault();
    e.stopPropagation();
    openSubmenuId = openSubmenuId === id ? null : id;
  }

  function updateOpenSubmenu() {
    if (isOnPdfProcessingPage || isOnWebScrapingPage) {
      openSubmenuId = "features";
    } else if (isOnEcommercePage) {
      openSubmenuId = "usecases";
    } else if (isAboutPage || isBlogPage) {
      openSubmenuId = "company";
    }
  }

  $effect(() => {
    currentPath = page.url.pathname;
    updateOpenSubmenu();
  });

  onMount(() => {
    updateOpenSubmenu();
  });

  const navigationItems = $derived((): NavigationItemConfig[] => [
    {
      id: "chatsheet",
      href: routes.root,
      label: "Chatsheet",
      icon: {
        component: sheetIcon,
        // strokeWidth: isTablesPage || isRootPage ? 1.5 : 1,
        isCustom: true,
      },
      onClick: handleTablesNavigation,
      isActive: isTablesPage || isRootPage,
      tooltipContent: "Chatsheet",
      altShortcutHotKey: "1",
      showLabel: true,
    },
    {
      id: "features",
      href: "/features",
      label: "Features",
      icon: {
        component: Feather,
        strokeWidth: isFeaturesPage && !isFeaturesSubpage ? 1.5 : 1,
      },
      onClick: (e) => toggleSubmenu(e, "features"),
      isActive: isFeaturesPage && !isFeaturesSubpage,
      tooltipContent: "Features",
      isCollapsible: true,
      submenu: [
        {
          href: "/features/ai-pdf-processing",
          label: "AI PDF Processing",
          icon: { component: FileText, strokeWidth: 1 },
          description: "Extract insights from PDF documents",
          onClick: (e) => handleNavigation(e, "/features/ai-pdf-processing"),
        },
        {
          href: "/features/ai-web-scraping",
          label: "AI Web Scraping",
          icon: { component: Globe, strokeWidth: 1 },
          description: "Gather data from websites automatically",
          onClick: (e) => handleNavigation(e, "/features/ai-web-scraping"),
        },
      ],
    },
    {
      id: "usecases",
      href: "/use-cases",
      label: "Use Cases",
      icon: {
        component: BookOpen,
        strokeWidth: isUseCasesPage && !isUseCasesSubpage ? 1.5 : 1,
      },
      onClick: (e) => toggleSubmenu(e, "usecases"),
      isActive: isUseCasesPage && !isUseCasesSubpage,
      tooltipContent: "Use Cases",
      isCollapsible: true,
      submenu: [
        {
          href: "/use-cases/ai-ecommerce-automation",
          label: "E-commerce Competitor Research",
          icon: { component: ShoppingCart, strokeWidth: 1 },
          description: "Analyze and track competitor strategies",
          onClick: (e) => handleNavigation(e, "/use-cases/ai-ecommerce-automation"),
        },
        {
          href: "/use-cases/recruiting-ai-resume-screening",
          label: "AI Resume Screening",
          icon: { component: FileUser, strokeWidth: 1 },
          description: "Review Resumes 100X Faster with AI",
          onClick: (e) => handleNavigation(e, "/use-cases/ai-ecommerce-automation"),
        },
      ],
    },
    {
      id: "pricing",
      href: routes.pricing,
      label: "Pricing",
      icon: {
        component: pricingIcon2,
        isCustom: true,
      },
      onClick: handlePricingNavigation,
      tooltipContent: "Pricing",
    },
    {
      id: "company",
      href: "#",
      label: "Company",
      icon: {
        component: companyIcon,
        isCustom: true,
        strokeWidth: 1,
      },
      onClick: (e) => toggleSubmenu(e, "company"),
      isActive: false,
      tooltipContent: "Company",
      isCollapsible: true,
      submenu: [
        {
          href: "/about",
          label: "About",
          icon: { component: Users, strokeWidth: 1 },
          description: "Learn about our mission",
          onClick: handleAboutNavigation,
        },
        {
          href: routes.blog,
          label: "Blog",
          icon: { component: FileText, strokeWidth: 1 },
          description: "Latest news and insights",
          onClick: handleBlogNavigation,
        },
      ],
    },
    {
      id: "search",
      href: $Current.isLoggedIn ? routes.searches : routes.root,
      label: "Search",
      icon: {
        component: UserRoundSearch,
        strokeWidth: isSearchPage || isRootPage ? 1.5 : 1,
      },
      onClick: handleSearchNavigation,
      isActive: isSearchPage,
      tooltipContent: "Search",
      isDisplayed: $Current.isLoggedIn,
      altShortcutHotKey: "3",
      showLabel: true,
    },
    {
      id: "templates",
      href: routes.templates,
      label: "Templates",
      icon: {
        component: Library,
        strokeWidth: isTemplatesPage ? 1.5 : 1,
      },
      onClick: handleTemplatesNavigation,
      isDisplayed: $Current.isLoggedIn,
      isActive: isTemplatesPage,
      tooltipContent: "Templates",
      altShortcutHotKey: "2",
      showLabel: true,
    },
    {
      id: "campaigns",
      href: routes.campaigns,
      label: "Campaigns",
      icon: {
        component: ScrollText,
        strokeWidth: isCampaignPage ? 1.5 : 1,
      },
      onClick: handleCampaignNavigation,
      isDisplayed: false, //$Current.isLoggedIn && (showAdminContent || isDevelopment()),
      tooltipContent: "Campaigns",
      isActive: isCampaignPage,
      inDevelopment: true,
      showLabel: true,
    },
    {
      id: "notebook",
      href: "/notebook",
      label: "Notebook",
      icon: {
        component: Notebook,
        strokeWidth: isNotebookPage ? 1.5 : 1,
      },
      onClick: handleNotebookNavigation,
      isActive: isNotebookPage,
      inDevelopment: true,
      isDisplayed: false, //!sidebar.isMobile && $Current.isLoggedIn && (showAdminContent || isDevelopment()),
      tooltipContent: "Notebook",
      showLabel: true,
    },
    {
      id: "sandbox",
      href: "/sandbox",
      label: "Sandbox",
      icon: {
        component: Microscope,
        strokeWidth: 1,
      },
      onClick: handleSandboxNavigation,
      inDevelopment: true,
      isDisplayed: !sidebar.isMobile && $Current.isLoggedIn && (showAdminContent || isDevelopment()),
      tooltipContent: "Sandbox",
      showLabel: true,
    },
    {
      id: "admin",
      href: "#",
      label: "Admin",
      icon: {
        component: Radar,
        strokeWidth: isAdminPage ? 1.5 : 1,
      },
      onClick: handleAdminNavigation,
      isDisplayed: showAdminContent,
      isActive: isAdminPage,
      tooltipContent: "Admin",
      extraProps: {
        "data-sveltekit-preload-data": false,
      },
      showTooltip: true,
      showLabel: true,
    },
    {
      id: "notifications",
      href: "#",
      label: "Notifications",
      icon: {
        component: Bell,
      },
      isDisplayed: true,
      extraProps: {
        "data-sveltekit-preload-data": false,
      },
    },
    {
      id: "credits",
      href: "#",
      label: "Credits",
      view: CreditsMenuItem,
      isDisplayed: !sidebar.isMobile && $Current.isLoggedIn,
      extraProps: {
        "data-sveltekit-preload-data": false,
      },
    },
    {
      id: "upgrade",
      href: "#",
      label: "Upgrade",
      icon: {
        component: upgradeIcon,
        isCustom: true,
      },
      onClick: handleUpgradeNavigation,
      isDisplayed: $Current.isLoggedIn && $Current.user?.plan !== SUBSCRIPTION_TIER.MAX,
      tooltipContent: "Upgrade",
      extraProps: {
        "data-sveltekit-preload-data": false,
      },
      showTooltip: true,
    },
  ]);

  let filteredItems = $derived(
    !onlyItems?.length
      ? navigationItems()
      : navigationItems().filter((item) => onlyItems.some((entry: string | NavigationItemConfig) => entry === item.id)),
  );

  type PopoverType = "credits" | "notifications";

  function handlePopoverHover(activePopover?: PopoverType) {
    const popovers: Record<PopoverType, boolean> = {
      credits: false,
      notifications: false,
    };

    if (activePopover) {
      popovers[activePopover] = true;
    }

    isCreditsOpen = popovers.credits;
    isNotificationsOpen = popovers.notifications;
  }
</script>

<div class={cn("text-sm")}>
  {#each filteredItems as item}
    {#if item.isDisplayed === undefined || item.isDisplayed}
      <!-- Normal menu item -->
      <div class={cn(getNavItemClasses(item.inDevelopment, item.isActive), item.altShortcutHotKey ? `relative` : "")}>
        {#if item.altShortcutHotKey && $tableStore.isInShortcutMode && !$tableStore.currentShortcutSequence}
          <ShortcutKey className="absolute right-0 top-0 size-4" key={item.altShortcutHotKey} />
        {/if}
        {#if item.label === "Credits"}
          <Popover.Root open={isCreditsOpen} onOpenChange={(open) => (isCreditsOpen = open)}>
            <div class="h-full w-full">
              <Popover.Trigger
                class="h-full w-full focus:outline-none"
                onmouseenter={() => {
                  handlePopoverHover("credits");
                }}
              >
                {#if item.icon}
                  {@const { Component, props } = renderIcon(item.icon)}
                  <div class={cn("flex h-full w-full items-center justify-center gap-3.5 uppercase")}>
                    <Component {...props} />
                    <span class:hidden={isCollapsed} class="text-nowrap">{item.label}</span>
                  </div>
                {:else if item.view}
                  <div class={cn("flex h-full w-full items-center justify-center gap-3.5 uppercase")}>
                    <item.view current={$sidePanelStore.credits} />
                    <span class:hidden={isCollapsed} class="text-nowrap">{item.label}</span>
                  </div>
                {/if}
              </Popover.Trigger>
              <Popover.Content
                onmouseleave={() => (isCreditsOpen = false)}
                side="right"
                align="center"
                sideOffset={5}
                class="bg-transpatent w-48 p-0 shadow-none dark:border-white/20"
              >
                <CreditsPreviewDialog />
              </Popover.Content>
            </div>
          </Popover.Root>
        {:else if item.label === "Notifications"}
          <Popover.Root open={isNotificationsOpen} onOpenChange={(open) => (isNotificationsOpen = open)}>
            <div class="h-full w-full">
              <Popover.Trigger
                class={cn(
                  "flex h-full w-full items-center justify-center text-theme-text-secondary focus:outline-none",
                  isCollapsed ? "flex-col gap-1" : "gap-3.5",
                )}
                onmouseenter={() => {
                  handlePopoverHover("notifications");
                }}
              >
                {#if item.icon}
                  {@const { Component, props } = renderIcon(item.icon)}
                  <Component {...props} />
                  {#if isCollapsed && item.showLabel}
                    <span class="text-nowrap text-[6px] uppercase">{item.label}</span>
                  {:else}
                    <span class:hidden={isCollapsed} class="text-nowrap">{item.label}</span>
                  {/if}
                {/if}
              </Popover.Trigger>
              <Popover.Content
                onmouseleave={() => (isNotificationsOpen = false)}
                side="right"
                align="start"
                sideOffset={5}
                class="bg-transpatent w-64 p-0 shadow-none dark:border-white/20"
              >
                <NotificationsDialog />
              </Popover.Content>
            </div>
          </Popover.Root>
        {:else if item.isCollapsible}
          <!-- Collapsible menu item -->
          <button
            class={cn(
              "flex h-full w-full items-center justify-center uppercase focus:outline-none",
              isCollapsed ? "flex-col gap-1" : "gap-3.5",
            )}
            onclick={(e) => item.onClick && item.onClick(e)}
          >
            <div
              class={cn(
                "flex h-full w-full items-center justify-center uppercase focus:outline-none text-black dark:text-white",
                isCollapsed ? "flex-col gap-1" : "justify-start gap-3.5",
              )}
            >
              {#if item.icon}
                {@const { Component, props } = renderIcon(item.icon)}
                <Component {...props} />
              {/if}
              <span class:hidden={isCollapsed} class="text-nowrap">{item.label}</span>
            </div>
            {#if !isCollapsed}
              {#if openSubmenuId === item.id}
                <ChevronDown class="h-4 w-4" />
              {:else}
                <ChevronRight class="h-4 w-4" />
              {/if}
            {/if}
          </button>
        {:else}
          <!-- Regular menu item -->
          {#if item.showTooltip}
            <Tooltip
              content={item.tooltipComponent ? undefined : item.tooltipContent || item.label}
              side="right"
              className="h-full w-hull flex items-center"
              tooltipComponent={item.tooltipComponent}
            >
              {#if item.icon}
                {@const { Component, props } = renderIcon(item.icon)}
                <a
                  href={item.href}
                  class={cn(
                    "flex h-full w-full items-center justify-center uppercase focus:outline-none",
                    isCollapsed ? "flex-col gap-1" : "gap-3.5",
                  )}
                  onclick={item.onClick}
                  {...item.extraProps || {}}
                  onmouseenter={() => {
                    handlePopoverHover();
                  }}
                >
                  <Component {...props} />
                  {#if isCollapsed && item.showLabel}
                    <span class="text-nowrap text-[6px]">{item.label}</span>
                  {:else}
                    <span class:hidden={isCollapsed} class="text-nowrap">{item.label}</span>
                  {/if}
                </a>
              {/if}
            </Tooltip>
          {:else if item.icon}
            {@const { Component, props } = renderIcon(item.icon)}
            <a
              href={item.href}
              class={cn(
                "flex h-full w-full items-center justify-center uppercase focus:outline-none",
                isCollapsed ? "flex-col gap-1" : "justify-start gap-3.5",
              )}
              onclick={item.onClick}
              {...item.extraProps || {}}
              onmouseenter={() => {
                handlePopoverHover();
              }}
            >
              <Component {...props} />
              {#if isCollapsed && item.showLabel}
                <span class="text-nowrap text-[6px]">{item.label}</span>
              {:else}
                <span class:hidden={isCollapsed} class="text-nowrap">{item.label}</span>
              {/if}
            </a>
          {/if}
        {/if}
      </div>

      <!-- Submenu for collapsible items with slide animation -->
      {#if !isCollapsed && item.isCollapsible && item.submenu && openSubmenuId === item.id}
        <div transition:slide={{ duration: 200 }}>
          {#each item.submenu as subItem}
            <div class={getNavItemClasses(false, isSubmenuItemActive(subItem.href), true)}>
              <a
                href={subItem.href}
                class={cn("flex h-full w-full items-center gap-3.5 uppercase")}
                onclick={subItem.onClick}
              >
                {#if subItem.icon}
                  {@const { Component, props } = renderIcon(subItem.icon)}
                  <Component {...props} />
                {/if}
                <span class="text-nowrap text-xs">{subItem.label}</span>
              </a>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  {/each}
</div>
